import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CONTEXTS, COMPASS_DIRECTIONS, getSkillDetails } from '@/data/categories';
import { Check, Copy, CheckCircle2 } from 'lucide-react';

export const CategoryResultCard = ({ 
  category,
  skillName, 
  context, 
  isUsed, 
  onMarkUsed, 
  onCopyShare,
  isDemo = false
}) => {
  const [copied, setCopied] = useState(false);
  
  if (!category || !skillName) return null;

  const skill = getSkillDetails(skillName);
  if (!skill) return null;

  const contextLabel = CONTEXTS.find(c => c.id === context)?.label || 'Daily life';
  const rep = skill.reps[context] || skill.reps.daily_life;
  const compass = COMPASS_DIRECTIONS[skill.compass_lens];

  const handleCopy = async () => {
    const shareText = `Today I'm practicing ${skillName} from the ${category} category.`;
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      if (onCopyShare) onCopyShare();
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Card className="w-full card-animated shadow-lg border-border bg-card overflow-hidden">
      {/* Accent bar at top */}
      <div className="h-1 bg-gradient-to-r from-primary via-accent to-primary" />
      
      <CardContent className="p-6 sm:p-10">
        {/* Category Section - Small label + medium heading */}
        <div className="text-center mb-4">
          <p className="text-xs text-helper uppercase tracking-widest mb-2 font-medium">
            Today&apos;s category
          </p>
          <h2 className="text-lg sm:text-xl font-semibold text-h2">
            {category}
          </h2>
        </div>

        {/* Visual separator */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-12 h-px bg-border" />
          <div className="w-2 h-2 rounded-full bg-primary/30" />
          <div className="w-12 h-px bg-border" />
        </div>

        {/* Skill Section - THE MAIN PAYOFF - Large bold text */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-h1 mb-4 leading-tight">
            {skillName}
          </h1>
          <p className="text-base sm:text-lg text-body leading-relaxed max-w-lg mx-auto">
            {skill.definition}
          </p>
          {isUsed && !isDemo && (
            <Badge 
              className="mt-4 bg-success/10 text-success border-success/20 px-4 py-1"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Done for today
            </Badge>
          )}
        </div>

        {/* Use It Today Section - Short prompt beneath */}
        <div className="bg-secondary/50 rounded-2xl p-6 sm:p-8 mb-6">
          <h3 className="text-sm font-bold text-h2 uppercase tracking-wide mb-4 text-center">
            Use it today
          </h3>
          <p className="text-body leading-relaxed text-center mb-6">
            Bring this skill into one decision, conversation, or task before the day ends.
          </p>
          
          {/* Context-specific action */}
          <div className="bg-card rounded-xl p-5 border border-border shadow-sm">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Badge variant="outline" className="text-xs font-medium">
                {contextLabel}
              </Badge>
              <span className="text-xs text-helper">•</span>
              <span className="text-xs text-helper font-medium">1-3 min</span>
            </div>
            <p className="text-body font-medium text-center leading-relaxed">{rep}</p>
          </div>
        </div>

        {/* Voice Line - Inspirational touch */}
        {skill.voice_line && (
          <div className="bg-accent/5 border border-accent/15 rounded-xl p-5 mb-6">
            <p className="text-body italic text-center leading-relaxed text-helper">
              &ldquo;{skill.voice_line.split(' - ')[1] || skill.voice_line}&rdquo;
            </p>
          </div>
        )}

        {/* Easier Option */}
        <div className="text-center mb-6">
          <p className="text-xs text-helper uppercase tracking-wide mb-2 font-medium">
            Even easier
          </p>
          <p className="text-sm text-body">{skill.easier_action}</p>
        </div>

        {/* Compass - Subtle detail */}
        <div className="flex items-center justify-center gap-3 p-4 rounded-lg bg-secondary/30 mb-6">
          <Badge 
            className="shrink-0 text-xs font-medium"
            style={{ 
              backgroundColor: `hsl(var(--compass-${skill.compass_lens}) / 0.12)`,
              color: `hsl(var(--compass-${skill.compass_lens}))`,
              border: 'none'
            }}
          >
            {compass?.name}
          </Badge>
          <p className="text-sm text-helper italic">{skill.compass_question}</p>
        </div>

        {/* Action Buttons */}
        {!isDemo && (
          <div className="flex flex-wrap gap-3 justify-center pt-4">
            <Button
              variant="outline"
              size="default"
              onClick={handleCopy}
              className="flex items-center gap-2 px-6"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-success" />
                  <span>Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>Share</span>
                </>
              )}
            </Button>
            
            {!isUsed && (
              <Button
                variant="default"
                size="default"
                onClick={onMarkUsed}
                className="flex items-center gap-2 px-6 bg-primary hover:bg-primary-hover"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>Mark done</span>
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CategoryResultCard;
